<template>
  <div class="min-h-screen bg-primary w-full h-full mt-[60px] lg:mt-0 themeTransition">
    <div v-if="!isLoading" class="sectionContainer place-content-start pt-[100px]">
      <page-wrapper v-if="pageGUI" :page-gui="pageGUI">
        <!-- content begin -->
        <div
          class="col-start-2 col-span-12 flex flex-col lg:flex-row md:col-start-2 md:col-span-10 lg:col-start-2 lg:col-span-11 lg:mb-[120px]"
        >
          <header1
            v-if="properties?.title"
            :text="properties?.title"
            :isUmbraco="isUmbraco"
            :theme="themeType"
            classes="mb-[60px] lg:mb-30"
          />
        </div>

        <div
          class="relative flex overflow-x-auto 2xl:overflow-x-visible no-scrollbar col-start-1 lg:col-start-3 col-span-full mb-10 lg:mb-[60px]"
        >
          <filter-tag
            v-for="tag in tags"
            :label="tag.title"
            :selected="tag.id === activeFilter"
            theme="light"
            classes="mr-3 shrink-0 first:mx-5 md:first:ml-8 md:last:mr-8 lg:last:mr-[108px] lg:first:ml-0"
            @handleClick="activeFilter = tag.id"
          />
        </div>
        <Transition
          appear
          enter-active-class="transition duration-500 ease-out-sine"
          leave-active-class="transition-all duration-300 ease-out-sine"
          enter-from-class="opacity-0 "
          leave-to-class="opacity-0 "
          :key="activeFilter"
        >
          <div class="col-start-2 col-span-12 pb-[60px] lg:col-start-3 lg:col-span-10">
            <accordion-cell
              v-for="item in officesArray"
              :title="item.country"
              :is-umbraco="isUmbraco"
              :themeType="themeType"
              :tag="item.phoneNumber"
            >
              <div
                v-for="office in item.officeLocations"
                class="mt-6 lg:mt-10 flex flex-col lg:flex-row lg:justify-between grow mr-[45px] mb-7"
              >
                <div class="flex-1">
                  <p :class="[textColor, `text-sm font-bold mb-2`]">
                    {{ office.title }}
                  </p>
                  <rich-text
                    :text="office.address"
                    :is-umbraco="isUmbraco"
                    :theme="themeType"
                    classes=""
                  />
                </div>
                <div class="flex-1">
                  <a
                    :href="`tel:${office.phone}`"
                    v-if="office.phone"
                    :class="[textColor, `text-sm font-bold mb-3 block hover:underline`]"
                  >
                    {{ office.phone }}
                  </a>
                  <a
                    v-if="office.email"
                    :href="`mailto:${office.email}`"
                    :class="[textColor, `text-sm font-bold mb-3 block hover:underline`]"
                  >
                    {{ office.email }}
                  </a>
                  <a
                    :href="officeWebsite(office.website)"
                    v-if="office.website"
                    :class="[textColor, `text-sm font-bold mb-3 block hover:underline`]"
                  >
                    {{ office.website }}
                  </a>
                  <rich-text
                    v-if="office.description"
                    :text="office.description"
                    :is-umbraco="isUmbraco"
                    :theme="themeType"
                    classes=""
                  />
                </div>
              </div>
            </accordion-cell>
          </div>
        </Transition>
        <!-- content end -->
      </page-wrapper>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { IOffice, IOfficePage } from "@/types/IOfficePage";
import { IFilterTag, Theme } from "@/types/types";
import {
  getOfficeByLocation,
  getOfficeLocations,
  getPage,
  getPageByID,
  getOfficeLocationsAPI,
} from "@/util/api";
import { changeTheme, getThemeType, parseUrl } from "@/util/helper";
import { computed, onBeforeMount, ref, watch } from "vue";
import Header1 from "@components/Typography/Header1.ce.vue";
import PageWrapper from "@components/PageWrapper.vue";
import FilterTag from "@components/Atoms/FilterTag/FilterTag.ce.vue";
import RichText from "@components/Typography/RichText.ce.vue";
import AccordionCell from "@molecules/Accordion/AccordionCell.vue";

const isLoading = ref(true);
const isUmbraco = ref(false);

const environment = ref();
const urlParams = ref<String[] | null>(null);

const pageGUI = ref<string | undefined>();
const pageData = ref<IOfficePage | null>(null);
const offices = ref<IOffice[] | null>(null);

const tags = ref<IFilterTag[] | null>(null);
const activeFilter = ref("");

const theme = ref<Theme | null>(null);

const isPreview = ref<boolean>(false);
const previewUrl = ref<string>("");
const pageKey = ref<string | undefined>();
const locationtags = ref<IFilterTag[] | null>(null);

onBeforeMount(async () => {
  // locationtags.value = await getOfficeLocationsAPI();
  //
  //pageKey.value = parseUrl(window.location.href).key;
  pageKey.value = document.documentElement.dataset.id;
  //get the meta property og:Url from the head tag
  const metaUrl = document.head.querySelector("meta[property='og:url']");
  if (metaUrl instanceof HTMLMetaElement) {
    previewUrl.value = metaUrl.content.replace(/^https?:\/\/[^/]+/, "");
  }

  environment.value = parseUrl(window.location.href).environment;
  urlParams.value = parseUrl(window.location.href).params;
  if (!urlParams.value) return;
  let path = urlParams.value.join("/");
  if (previewUrl.value.indexOf(path) < 0) {
    path = previewUrl.value;
    isPreview.value = true;
  }
  if (path) {
    pageData.value = (await getPageByID(pageKey.value, isPreview.value)) as IOfficePage;
    //pageData.value = (await getPage(path, isPreview.value)) as IOfficePage;
    tags.value = await getOfficeLocationsAPI();

    if (tags.value) {
      activeFilter.value = tags.value[0].id;
    }
    if (pageData.value) {
      theme.value = pageData.value.properties.backgroundColor.label;
      if (theme.value) changeTheme(theme.value);
      pageGUI.value = pageData.value.route.startItem.id;

      if (activeFilter.value) {
        offices.value = await getOfficeByLocation(activeFilter.value);
      }

      isLoading.value = false;
    }
  }
});

watch(activeFilter, (value) => {
  if (value) {
    getOfficeByLocation(activeFilter.value).then((data) => {
      if (!offices) return;
      offices.value = data;
    });
  }
});

const officesArray = computed(() => {
  if (!offices.value) return [];

  const officesAray = offices.value.map((office) => {
    const { country, offices } = office;
    const officeLocations = offices.map((office) => {
      return {
        title: office.title,
        address: office.address,
        phone: office.phoneNumber,
        email: office.email,
        website: office.website,
        description: office.content,
      };
    });
    return {
      country: country.countryName,
      phoneNumber: country.mainOfficeNumber,
      officeLocations,
    };
  });
  return officesAray;
});

const properties = computed(() => {
  return pageData.value?.properties;
});

const themeType = computed(() => {
  if (!theme.value) return "dark";
  return getThemeType(theme.value);
});

const textColor = computed(() => {
  return isUmbraco.value && themeType.value === "dark"
    ? "text-white"
    : isUmbraco.value && themeType.value === "light"
    ? "text-aubergine"
    : "text-typography";
});

const officeWebsite = (website: string) => {
  if (website.startsWith("http")) {
    return website;
  } else {
    return `https://${website}`;
  }
};
</script>
